import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Helmet } from "react-helmet";
import { useTranslationApp } from "../../lib/i18next";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import { IS_ADLE_DOMAIN } from "../../utils/constants";
import {
  normalizeAppLanguage,
  persistAppLanguage,
} from "../../utils/i18nLanguage";
import {
  AI_COMPETITORS_AGENDA_CONTEXT,
  AI_COMPETITORS_AGENDA_CONTEXT_FALLBACK,
  AI_COMPETITORS_AGENDA_CONTEXT_ZERO,
  AI_COMPETITORS_AGENDA_MUTED,
  AI_COMPETITORS_AGENDA_STEP_NUMBER,
  AI_COMPETITORS_AGENDA_TITLE,
  AI_COMPETITORS_PAGE_TITLE,
} from "../../i18n/keysTranslations";
import AiCompetitorsHeader from "./components/AiCompetitorsHeader";
import AiCompetitorsStepIntro from "./components/AiCompetitorsStepIntro";
import { useAiCompetitorsSessionListener } from "./hooks/useAiCompetitorsSessionListener";
import {
  getAiCompetitorsCalLink,
  AI_COMPETITORS_CAL_BOOKING_SUCCESS_ACTIONS,
  AI_COMPETITORS_CAL_NAMESPACE,
} from "./aiCompetitorsConstants";
import { redirectToAiCompetitorsThanksAfterBooking } from "./aiCompetitorsThanksRedirect";
import { formatCompetitorNamesList } from "./aiCompetitorsAds";
import { toDomainSessionId } from "./aiCompetitorsBrand";

const getCalIframeHeight = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.iframeHeight,
    payload.data?.iframeHeight,
    payload.arg?.iframeHeight,
    payload.detail?.iframeHeight,
    payload.detail?.data?.iframeHeight,
  ];
  for (const value of candidates) {
    const height = Number(value);
    if (Number.isFinite(height) && height > 0) return Math.ceil(height);
  }
  return null;
};

const applyCalEmbedHeight = (root, height) => {
  if (!root) return;
  const nextHeight = Math.ceil(Number(height));
  if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
  const nextHeightPx = `${nextHeight}px`;
  const iframe = root.querySelector("iframe");
  if (iframe) {
    iframe.style.setProperty("height", nextHeightPx, "important");
    iframe.style.setProperty("max-height", "none", "important");
    iframe.style.setProperty("overflow", "hidden", "important");
    iframe.setAttribute("scrolling", "no");
  }
  const calInline = root.querySelector("cal-inline");
  if (calInline) {
    calInline.style.setProperty("height", nextHeightPx);
    calInline.style.setProperty("max-height", "none", "important");
    calInline.style.setProperty("min-height", "0", "important");
    calInline.style.setProperty("overflow", "visible", "important");
  }
};

const LANGUAGE_SWITCHER_STYLES = {
  position: "fixed",
  top: { xs: 12, sm: 40 },
  right: { xs: 24, sm: 16 },
  zIndex: 1300,
};

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const AiCompetitorsAgenda = () => {
  const { t, i18n } = useTranslationApp();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const brandQuery = searchParams.get("brand") || "";
  const sessionId =
    location.state?.sessionId || toDomainSessionId(brandQuery) || "";
  const currentLanguage = normalizeAppLanguage(i18n.language) || "en";
  const productName = IS_ADLE_DOMAIN ? "Adle" : "Buzzly";
  const calNamespace = `${AI_COMPETITORS_CAL_NAMESPACE}-${currentLanguage}`;

  const session = useAiCompetitorsSessionListener({
    sessionId,
    language: currentLanguage,
    isActive: Boolean(sessionId),
    fetchOnce: true,
  });
  const hasOpenedThanksRef = useRef(false);
  const calEmbedRef = useRef(null);
  const calLink = getAiCompetitorsCalLink(currentLanguage);

  const onBookingSuccessful = useCallback(() => {
    if (hasOpenedThanksRef.current) return;
    hasOpenedThanksRef.current = true;
    redirectToAiCompetitorsThanksAfterBooking({
      sessionId,
      language: currentLanguage,
      calLink,
    });
  }, [calLink, currentLanguage, sessionId]);

  useEffect(() => {
    let isMounted = true;
    const onInitCalUi = async () => {
      const cal = await getCalApi({ namespace: calNamespace });
      if (!isMounted) return;
      cal("ui", {
        theme: "light",
        hideEventTypeDetails: false,
        styles: {
          body: { background: "transparent" },
        },
      });
      AI_COMPETITORS_CAL_BOOKING_SUCCESS_ACTIONS.forEach((action) => {
        cal("on", {
          action,
          callback: onBookingSuccessful,
        });
      });
      cal("on", {
        action: "__dimensionChanged",
        callback: (event) => {
          applyCalEmbedHeight(calEmbedRef.current, getCalIframeHeight(event));
        },
      });
    };
    onInitCalUi();

    const onCalMessage = (event) => {
      const payload = event?.data;
      const action =
        payload?.type ||
        payload?.action ||
        payload?.name ||
        payload?.method ||
        payload?.data?.type;
      if (action === "__dimensionChanged" || payload?.originator === "CAL") {
        applyCalEmbedHeight(calEmbedRef.current, getCalIframeHeight(payload));
      }
      if (!AI_COMPETITORS_CAL_BOOKING_SUCCESS_ACTIONS.includes(action)) return;
      onBookingSuccessful();
    };
    window.addEventListener("message", onCalMessage);

    return () => {
      isMounted = false;
      window.removeEventListener("message", onCalMessage);
    };
  }, [calNamespace, onBookingSuccessful]);

  const competitorNames = formatCompetitorNamesList(
    session?.adsSummary?.competitorNames ||
      (session?.competitors || []).map((competitor) => competitor.name),
    currentLanguage
  );
  const adsCount = Number(session?.adsSummary?.adsCount || 0);

  const contextCopy = useMemo(() => {
    if (adsCount > 0 && competitorNames) {
      return t(AI_COMPETITORS_AGENDA_CONTEXT, {
        adsCount,
        competitorNames,
        productName,
      });
    }
    if (session?.adsSummary && adsCount === 0) {
      return t(AI_COMPETITORS_AGENDA_CONTEXT_ZERO, { productName });
    }
    return t(AI_COMPETITORS_AGENDA_CONTEXT_FALLBACK, { productName });
  }, [adsCount, competitorNames, productName, session?.adsSummary, t]);

  const calMetadata = {
    sessionId: sessionId || "",
    websiteUrl: session?.websiteUrl || "",
    brandName: session?.brand?.name || "",
    brandCategory: session?.brand?.category || "",
    brandCountry: session?.brand?.country || "",
    competitors: competitorNames,
    adsCount: String(adsCount),
  };

  const onChangeLanguage = (language) => {
    const nextLanguage = normalizeAppLanguage(language);
    if (!nextLanguage || nextLanguage === currentLanguage) return;
    i18n.changeLanguage(nextLanguage);
    persistAppLanguage(nextLanguage);
  };

  return (
    <>
      <Helmet>
        <title>{t(AI_COMPETITORS_PAGE_TITLE)}</title>
      </Helmet>
      <Box sx={LANGUAGE_SWITCHER_STYLES}>
        <LanguageSwitcher
          language={currentLanguage}
          onChangeLanguage={onChangeLanguage}
          colorButton="secondary"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
        />
      </Box>
      <Box
        component="main"
        sx={{
          minHeight: "100dvh",
          bgcolor: "background.default",
          color: "text.primary",
          overflow: "visible",
          px: { xs: 3, sm: 3 },
          pt: { xs: 1.25, sm: 3 },
          pb: { xs: "max(16px, env(safe-area-inset-bottom))", sm: 6 },
        }}
      >
        <Box
          component="section"
          sx={{
            mx: "auto",
            display: "flex",
            width: "100%",
            maxWidth: "880px",
            flexDirection: "column",
          }}
        >
          <AiCompetitorsHeader
            compact
            sx={{ mb: { xs: 1, sm: 2 }, pr: { xs: 6, sm: 0 } }}
          />
          <Fade in timeout={500}>
            <Box>
              <AiCompetitorsStepIntro
                stepNumber={t(AI_COMPETITORS_AGENDA_STEP_NUMBER)}
                title={t(AI_COMPETITORS_AGENDA_TITLE)}
                mutedTitle={t(AI_COMPETITORS_AGENDA_MUTED)}
                description={contextCopy}
              />
              <Box
                ref={calEmbedRef}
                sx={{
                  mt: { xs: 2, sm: 4 },
                  position: "relative",
                  width: "100%",
                  overflow: "visible",
                  "& .cal-inline-container": {
                    height: "auto !important",
                    maxHeight: "none !important",
                    minHeight: "0 !important",
                    overflow: "visible !important",
                  },
                  "& cal-inline": {
                    maxHeight: "none !important",
                    minHeight: "0 !important",
                    overflow: "visible !important",
                  },
                  "& iframe": {
                    width: "100% !important",
                    display: "block",
                    overflow: "hidden !important",
                    maxHeight: "none !important",
                  },
                }}
              >
                <Cal
                  key={`${calLink}-${currentLanguage}-${isMobile ? "mobile" : "desktop"}`}
                  namespace={calNamespace}
                  calLink={calLink}
                  style={{
                    width: "100%",
                    height: "auto",
                    overflow: "visible",
                  }}
                  config={{
                    layout: "month_view",
                    theme: "light",
                    hideEventTypeDetails: false,
                    notes: contextCopy,
                    metadata: JSON.stringify(calMetadata),
                    "ui.autoscroll": "false",
                    iframeAttrs: {
                      scrolling: "no",
                    },
                  }}
                />
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>
    </>
  );
};

export default AiCompetitorsAgenda;
